import React, { useState } from 'react';
import {
    useGetStorefrontCartsQuery,
    useGetStorefrontsQuery,
    useGetStorefrontCartsForStorefrontSearchQuery
} from '../../../store/endpoints/StorefrontSettingsEndpoints';
import { IStorefrontCart } from '../../../models/Storefront';
import StorefrontCartPanel from './StorefrontCartPanel';
import GridComposer from '../../GridComposer/GridComposer';
import { GridSelectionMode, IColumnDetails } from '../../../models/GridConfiguration';
import { PrimaryButton } from '@fluentui/react';

interface StorefrontCartsTableProps {
    storefront?: string;
}

const StorefrontCartsTable: React.FC<StorefrontCartsTableProps> = ({ storefront }) => {
    const [selectedCartId, setSelectedCartId] = useState<string | undefined>(undefined);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [filter, setFilter] = useState<string>();
    const [filterField, setFilterField] = useState<string>();
    const [sortBy, setSortBy] = useState<string>();
    const [sortDirection, setSortDirection] = useState<string>();

    const conditions = { page, pageSize, searchTerm: filter, searchBy: filterField, sortBy, sortDirection };
    const globalCarts = useGetStorefrontCartsQuery({ conditions }, { skip: !!storefront });
    const scopedCarts = useGetStorefrontCartsForStorefrontSearchQuery({ storefrontId: storefront ?? '', conditions }, { skip: !storefront });
    const carts = storefront ? scopedCarts : globalCarts;
    const possibleStorefronts = useGetStorefrontsQuery();

    const getCustomColumns = (): IColumnDetails[] => [
        {
            index: 0,
            identifier: 'Details',
            label: '',
            type: '',
            component: () => <PrimaryButton>Details</PrimaryButton>,
            onClick: (cart: IStorefrontCart) => setSelectedCartId(cart.id),
            required: true,
        },
        ...(!storefront ? [{
            identifier: 'storefront.keyword',
            label: 'Storefront',
            type: '',
            sortable: true,
            filterable: true,
            component: (cart: IStorefrontCart) => (
                <div>{possibleStorefronts.data?.items?.find(s => s.id === cart.storefront)?.name ?? cart.storefront}</div>
            ),
            customFilterOptions: () =>
                possibleStorefronts.data?.items?.map(s => ({ key: s.id ?? '', text: s.name ?? '' })),
            tooltip: 'Sort on Storefront is not alphabetical',
        } as IColumnDetails] : []),
    ];

    return (
        <div style={{ overflowX: 'auto' }}>
            <GridComposer
                data={carts.data ?? {}}
                page={page}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                loading={carts.isLoading || carts.isFetching}
                onRefresh={() => carts.refetch()}
                selectionMode={GridSelectionMode.multiple}
                columnSetName={'StorefrontCart'}
                filter={filter}
                filterField={filterField}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={(column, direction) => {
                    setSortBy(column);
                    setSortDirection(direction);
                }}
                onFilter={(f, column) => {
                    setFilter(f);
                    setFilterField(column);
                }}
                overrideColumns={getCustomColumns()}
                canDragColumns={true}
                canResizeColumns={true}
                canCustomizeColumns={true}
                canChangePageSize={true}
                canCreateCsv={true}
                getItemId={(item: IStorefrontCart) => item.id}
            />
            <StorefrontCartPanel
                cartId={selectedCartId}
                onDismiss={() => setSelectedCartId(undefined)}
            />
        </div>
    );
};

export default StorefrontCartsTable;
